import React, { useEffect, useState } from 'react';
import { Notice } from './notice';
import NoticeList from './notice-list';
import { REACT_APP_MARKETS_URL, AwdScriptObject } from './store-notices-url';
import packageJson from '../package.json';
import { StoreNotices } from './store-notices';
import { FaEdit, FaSave, FaTimes, FaPlus } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';

const App: React.FC = () => {
    const [notices, setStoreNotices] = useState<Notice[]>([]);
    const [confirmShown, setConfirmShown] = useState<boolean>(false);
    const [fetchResult, setFetchResult] = useState<string>("");
    const [editNoticeId, setEditNoticeId] = useState<string | null>(null);
    const allowFiltering = (editNoticeId === null);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isDefaultMessageEditMode, setIsDefaultMessageEditMode] = useState<boolean>(false);
    const [defaultMessage, setDefaultMessage] = useState<string>('');
    const [previousDefaultMessage, setPreviousDefaultMessage] = useState<string>(''); // Store previous default message
    const disableEditDefaultMessageButton = editNoticeId !== null; // Disable if any notice is being edited
    const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
    const [isDefaultMessageExpanded, setIsDefaultMessageExpanded] = useState<boolean>(false); // Ensure it starts expanded
    const [newNoticeId, setNewNoticeId] = useState<string | null>(null);

    const toggleDefaultMessageExpand = () => {
        if (!isDefaultMessageEditMode && !editNoticeId) {
            setIsDefaultMessageExpanded(!isDefaultMessageExpanded);
            if (!isDefaultMessageExpanded) {
                setExpandedItemId(null); // Collapse all notice items
            }
        }
    };

    useEffect(() => {
        if (!isDefaultMessageEditMode && !editNoticeId) {
            if (expandedItemId) {
                setIsDefaultMessageExpanded(false); // Collapse default message
            }
        }
    }, [expandedItemId, isDefaultMessageEditMode, editNoticeId]);

    useEffect(() => {
        if (localStorage.getItem('saveFailed') === 'true') {
            setStatusMessage('Saving to server failed. Local changes still available.');
            loadFromLocalStorage();
            setConfirmShown(true);
        } else {
            setStatusMessage('Loading...');
            fetchStoreNotices();
        }
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isDefaultMessageEditMode) {
                handleCancelDefaultMessage();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isDefaultMessageEditMode]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const query = event.target.value;
        setSearchQuery(query);
    };

    const fetchStoreNotices = () => {
        fetch(REACT_APP_MARKETS_URL, {
            method: 'GET',
            headers: {
                'X-WP-Nonce': AwdScriptObject.nonce,
                'Content-Type': 'application/json'
            }
        })
            .then(response => {
                setFetchResult(response.status.toString() + ' / ' + response.statusText.toString());
                return response.json();
            })
            .then(data => {
                setStatusMessage('');
                try {
                    const parsedData: StoreNotices = typeof data === 'string' ? JSON.parse(data) : data;
                    setDefaultMessage(parsedData.default_message);
                    const _notices: Notice[] = Array.isArray(parsedData.notices) ? parsedData.notices : [];
                    const updatedStoreNotices = _notices.map(notice => {
                        notice.notice_name = decodeURIComponent(encodeURIComponent(notice.notice_name));
                        if (!notice.id) {
                            notice.id = uuidv4();
                        }
                        return notice;
                    });

                    const sortedNotices = sortNotices(updatedStoreNotices);
                    setStoreNotices(sortedNotices);

                } catch (error) {
                    console.error('Error parsing notices data:', error);
                    setStatusMessage('Error parsing notices data.');
                }
            })
            .catch(error => {
                setStatusMessage('Error fetching notices. Verify URL: ' + REACT_APP_MARKETS_URL);
                console.error('Error fetching notices:', error);
            });
    };

    const refreshFromServer = () => {
        setConfirmShown(false);
        localStorage.removeItem('saveFailed');
        localStorage.removeItem('notices');
        fetchStoreNotices();
    };

    const loadFromLocalStorage = () => {
        const storedStoreNotices = localStorage.getItem('notices');
        if (storedStoreNotices) {
            setStoreNotices(JSON.parse(storedStoreNotices));
        }
    }

    const handleNoticeSave = (editedNotice: Notice) => {
        setStatusMessage('Saving...');
        setConfirmShown(false);
        setStoreNotices((prevStoreNotices: Notice[]) => {
            const updatedStoreNotices = prevStoreNotices.map(notice => {
                if (notice.id === editedNotice.id) {
                    // console.error('app Edited Notice:', editedNotice);
                    return editedNotice;
                }
                return notice;
            });
            retrySave(updatedStoreNotices, defaultMessage);
            return updatedStoreNotices;
        });
    };

    const handleNoticeEdit = (editedNoticeid: string | null) => {
        setEditNoticeId(editedNoticeid !== null ? editedNoticeid.toString() : null);
    };

    const sortNotices = (notices: Notice[]): Notice[] => {
        const currentDate = new Date().setHours(0, 0, 0, 0);
        return notices.sort((a, b) => {
            const startDateA = new Date(a.start_date).setHours(0, 0, 0, 0);
            const startDateB = new Date(b.start_date).setHours(0, 0, 0, 0);
            const endDateA = new Date(a.end_date).setHours(0, 0, 0, 0);
            const endDateB = new Date(b.end_date).setHours(0, 0, 0, 0);

            const isInProgressA = currentDate >= startDateA && currentDate <= endDateA;
            const isInProgressB = currentDate >= startDateB && currentDate <= endDateB;
            const isUpcomingA = startDateA > currentDate;
            const isUpcomingB = startDateB > currentDate;

            if (isInProgressA && !isInProgressB) return -1;
            if (!isInProgressA && isInProgressB) return 1;
            if (isUpcomingA && !isUpcomingB) return -1;
            if (!isUpcomingA && isUpcomingB) return 1;

            return startDateA - startDateB;
        });
    };

    const cancelNoticeSave = () => {
        if (newNoticeId !== null) {
            setStoreNotices((prevNotices) => prevNotices.filter(notice => notice.id !== newNoticeId));
            setNewNoticeId(null);
        }
        setEditNoticeId(null);
        setExpandedItemId(null);
    };

    const retrySave = (notices: Notice[], defaultMessage: string) => {
        const sortedNotices = sortNotices(notices);
        setStoreNotices(sortedNotices);
        const encodedStoreNotices = sortedNotices.map(notice => {
            return {
                ...notice,
                notice_name: decodeURIComponent(encodeURIComponent(notice.notice_name)),
            };
        });
        fetch(REACT_APP_MARKETS_URL, {
            method: 'POST',
            body: JSON.stringify({ notices: encodedStoreNotices, default_message: defaultMessage }),
            headers: {
                'X-WP-Nonce': AwdScriptObject.nonce,
                'Content-Type': 'application/json'
            }
        }).then(response => {
            setFetchResult(response.status.toString() + ' / ' + response.statusText.toString());
            if (!response.ok) {
                throw new Error(response.status.toString());
            } else {
                setConfirmShown(false);
                localStorage.removeItem('saveFailed');
                localStorage.removeItem('notices');
                setStatusMessage('Saved.');
                // Ensure the expanded state remains after saving
                setIsDefaultMessageExpanded(true);
            }
        })
            .catch(error => {
                console.error('Error saving notices to server:', error);
                localStorage.setItem('notices', JSON.stringify(notices));
                localStorage.setItem('saveFailed', 'true');
                setConfirmShown(true);
                setStatusMessage('Saving to server failed. Local changes still available.');
            });
    };

    const filteredStoreNotices = notices?.filter((notice: Notice) => {
        const matchesSearch = notice.notice_name.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch;
    });

    const getActiveNotice = (): Notice | null => {
        const currentDate = new Date();
        const currentDateOnly = new Date(currentDate.setHours(0, 0, 0, 0));
        for (const notice of notices) {
            const startDate = new Date(new Date(notice.start_date).setHours(0, 0, 0, 0));
            const endDate = new Date(new Date(notice.end_date).setHours(0, 0, 0, 0));
            if (currentDateOnly >= startDate && currentDateOnly <= endDate) {
                return notice;
            }
        }
        return {
            id: '0',
            notice_name: 'Default Notice',
            start_date: '',
            end_date: '',
            notice: defaultMessage,
            isActive: true
        };
    };

    const activeNotice = getActiveNotice();

    function handleDefaultMessageEdit(): void {
        setPreviousDefaultMessage(defaultMessage); // Store the previous default message
        setIsDefaultMessageEditMode(true);
        setIsDefaultMessageExpanded(true); // Ensure it is expanded while editing
    }

    function handleSaveDefaultMessage(): void {
        setStatusMessage('Saving...');
        setIsDefaultMessageEditMode(false);
        retrySave(notices, defaultMessage);
    }

    function handleCancelDefaultMessage(): void {
        setIsDefaultMessageEditMode(false);
        setDefaultMessage(previousDefaultMessage); // Restore the previous default message
        // Ensure the expanded state remains after canceling
        setIsDefaultMessageExpanded(true);
    }

    const handleNoticeCopy = (newNotice: Notice) => {
        setSearchQuery("");
        setNewNoticeId(newNotice.id);
        setStoreNotices((prevNotices) => {
            const updatedNotices = [newNotice, ...prevNotices];
            return updatedNotices;
        });
    };

    const handleNoticeDelete = (noticeId: string) => {
        const updatedNotices = notices.filter(notice => notice.id !== noticeId);
        setStoreNotices(updatedNotices);
        retrySave(updatedNotices, defaultMessage); // Save the default message along with notices
    };

    const handleNoticeItemExpand = (noticeId: string | null) => {
        if (!isDefaultMessageEditMode && !editNoticeId) {
            setExpandedItemId(noticeId);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, onClick: () => void) => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            onClick();
        }
    };

    const handleTextAreaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
        }
    };

    return (
        <>
            <div className="sticky-header">
                <h2>Store Notices {packageJson.version}</h2>
                <div className="filter-container">
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        const inputElement = (e.target as HTMLFormElement).querySelector('input');
                        if (inputElement) {
                            const currentValue = inputElement.value;
                            const dataList = document.createElement('datalist');
                            dataList.id = 'autocomplete-options';
                            const option = document.createElement('option');
                            option.value = currentValue;
                            dataList.appendChild(option);
                            inputElement.setAttribute('list', 'autocomplete-options');
                        }
                        if (filteredStoreNotices.length > 0) {
                            // setEditNoticeId(filteredStoreNotices[0].id);
                        }
                    }}>
                        <div className="filter-bar">
                            <input
                                type="text"
                                autoCapitalize='on'
                                autoComplete='on'
                                placeholder="Filter notices..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                list="autocomplete-options"
                                disabled={editNoticeId !== null || isDefaultMessageEditMode} // Disable input if a notice is in edit mode or default message is being edited
                                className="filter-input"
                            />
                            <datalist id="autocomplete-options">
                                {filteredStoreNotices.map((notice) => (
                                    <option key={notice.id} value={notice.notice_name} />
                                ))}
                            </datalist>
                            <div className="filter-results">
                                {filteredStoreNotices.length} of {notices.length}
                            </div>
                        </div>
                        {/* Submit button is not required as hitting enter will trigger the form submission */}
                    </form>
                </div>
                {localStorage.getItem('saveFailed') === 'true' && (
                    <div className="save-failed">
                        <div>
                            {statusMessage}
                        </div>
                        {confirmShown && (
                            <div className="button-container" style={{ background: allowFiltering ? 'lightyellow' : 'none' }}>
                                <button disabled={!allowFiltering} onClick={() => retrySave(notices, defaultMessage)} onKeyDown={(e) => handleKeyDown(e, () => retrySave(notices, defaultMessage))}><FaSave className="icon" />Retry Save</button>
                                <button disabled={!allowFiltering} onClick={refreshFromServer} onKeyDown={(e) => handleKeyDown(e, refreshFromServer)}><FaTimes className="icon" />Refresh</button>
                            </div>
                        )}
                    </div>
                )} {!localStorage.getItem('saveFailed') && (
                    <div>
                        {statusMessage}
                    </div>
                )}
            </div>
            <div className='content-container'>
                <div className="content-container-col1">
                    <div className="message-display-container">
                        {activeNotice ? (
                            <div>
                                <p>Today&rsquo;s message:</p>
                                {activeNotice ? activeNotice.notice_name : "Default message"}
                            </div>
                        ) : (
                            <div>
                                <p>{defaultMessage}</p>
                            </div>
                        )}
                    </div>
                    <div className={`market-item-container default-message-container ${isDefaultMessageEditMode ? 'editing' : ''} ${isDefaultMessageExpanded || isDefaultMessageEditMode ? 'expanded' : 'collapsed'}`} onClick={toggleDefaultMessageExpand} tabIndex={0}
                        role="button"
                        onKeyDown={(e) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                                e.preventDefault();
                                toggleDefaultMessageExpand();
                            }
                        }}
                    >
                        <div
                            className="notice-name"

                        >
                            Default Notice
                        </div>
                        {(isDefaultMessageExpanded || isDefaultMessageEditMode) && (
                            <div className="multiline-textarea">
                                <textarea
                                    id="default_message"
                                    name="default_message"
                                    value={defaultMessage}
                                    rows={4}
                                    onChange={(e) => setDefaultMessage(e.target.value)}
                                    readOnly={!isDefaultMessageEditMode}
                                    onKeyDown={handleTextAreaKeyDown}
                                />
                            </div>
                        )}
                        <div className="button-container">
                            {isDefaultMessageEditMode ? (
                                <>
                                    <button onClick={() => handleSaveDefaultMessage()} onKeyDown={(e) => handleKeyDown(e, handleSaveDefaultMessage)}>
                                        <FaSave className="icon" /> Save
                                    </button>
                                    <button onClick={() => handleCancelDefaultMessage()} onKeyDown={(e) => handleKeyDown(e, handleCancelDefaultMessage)}>
                                        <FaTimes className="icon" /> Cancel
                                    </button>
                                </>
                            ) : (
                                <>
                                    {isDefaultMessageExpanded && (
                                        <button onClick={() => handleDefaultMessageEdit()} disabled={disableEditDefaultMessageButton} onKeyDown={(e) => handleKeyDown(e, handleDefaultMessageEdit)}>
                                            <FaEdit className="icon" /> Edit
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <NoticeList
                        notices={filteredStoreNotices}
                        handleSave={handleNoticeSave}
                        handleCancel={cancelNoticeSave}
                        handleEdit={handleNoticeEdit}
                        handleCopy={handleNoticeCopy}
                        handleDelete={handleNoticeDelete}
                        expandedItemId={expandedItemId}
                        setExpandedItemId={setExpandedItemId}
                        disabled={isDefaultMessageEditMode} // Disable NoticeList while editing default message
                        handleExpandCollapse={handleNoticeItemExpand} // Add this line
                    />
                </div>
                <div className="content-container-col2">
                    <textarea className="debug" value={REACT_APP_MARKETS_URL} readOnly />
                    <textarea className="debug" value={fetchResult} readOnly />
                    <textarea className="debug-width" value={JSON.stringify(notices, null, 2)} readOnly />
                </div>
            </div>
        </>
    );
};

export default App;
